import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';

import { DataExportService } from '@connect-services/client/services/data-export.service';
import { SelectionStateService } from '@connect-services/client/services/map/selection/selection-state.service';
import { ExpansionPanelComponent } from '@connect-services/src/lib/components/expansion-panel/expansion-panel.component';
import {
    CustomColumn,
    CustomTableComponent,
    SortState,
} from '@connect-services/src/lib/components/table/table.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { OrderEnum } from '@shared/client/models/pagination.model';

export interface SelectedLayer {
    layerId: string;
    layerName: string;
    numberOfEntities: number;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CustomTableComponent, ExpansionPanelComponent, LoadingComponent, RessourcesModule],
    selector: 'app-entity-selection',
    templateUrl: './entity-selection.component.html',
})
export class EntitySelectionComponent {
    protected readonly selectedLayerRows = computed<SelectedLayer[]>(() => {
        const selectedLayers: SelectedLayer[] = this.buildRows(
            this.selectionStateService.selectedEntitiesByLayerId(),
            this.dataExportService.layerNameById(),
        );

        return this.applySort(selectedLayers, this.sortState());
    });
    protected readonly tableColumns: CustomColumn[] = [
        {
            key: 'layerName',
            label: 'layer',
            sortable: true,
        },
        {
            key: 'numberOfEntities',
            label: 'numberOfEntities',
            sortable: true,
        },
    ];

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly dataExportService = inject(DataExportService);

    private readonly selectionStateService = inject(SelectionStateService);
    private readonly sortState = signal<SortState | undefined>(undefined);

    protected onSort(sortState: SortState | undefined): void {
        this.sortState.set(sortState);
    }

    private applySort(rows: SelectedLayer[], sortState?: SortState): SelectedLayer[] {
        if (!sortState?.order) {
            return rows;
        }

        const factor = sortState.order === OrderEnum.ASC ? 1 : -1;

        if (sortState.orderBy === 'numberOfEntities') {
            return [...rows].sort((a, b) => factor * (a.numberOfEntities - b.numberOfEntities));
        }

        return [...rows].sort((a, b) => factor * a.layerName.localeCompare(b.layerName));
    }

    private buildRows(
        selectedEntitiesByLayerId: Record<string, unknown[]>,
        layerNameById: Map<string, string>,
    ): SelectedLayer[] {
        return Object.entries(selectedEntitiesByLayerId).map(([layerId, entities]: [string, unknown[]]) => ({
            layerId,
            layerName: layerNameById.get(layerId) ?? layerId,
            numberOfEntities: entities.length,
        }));
    }
}
